import { useEffect, useRef, useState } from 'react';
import { Bell, BellRing, CheckCircle2, AlertTriangle, AlertCircle, Phone, Truck } from 'lucide-react';
import { Link } from 'react-router-dom';
import { api, type Notification } from '@/lib/api';
import { cn } from '@/lib/utils';

const POLL_MS = 25_000;

interface Props {
  businessId: string;
}

/**
 * Bell with unread badge. Polls /notifications every 25s; opens a dropdown
 * listing recent notifications. Critical (red) entries surface dispatch
 * failures — the owner clicks straight into the affected job.
 */
export function NotificationBell({ businessId }: Props) {
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  async function reload() {
    try {
      const { notifications, unread_count } = await api.crm.notifications.list(businessId, { limit: 30 });
      setItems(notifications);
      setUnread(unread_count);
    } catch {
      /* swallow; show nothing if offline */
    }
  }

  useEffect(() => {
    reload();
    const t = window.setInterval(reload, POLL_MS);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const hasCritical = items.some(n => n.severity === 'critical' && !n.read_at);

  async function markAllRead() {
    await api.crm.notifications.markAllRead(businessId);
    reload();
  }

  async function handleOpen(n: Notification) {
    if (!n.read_at) {
      try { await api.crm.notifications.markRead(businessId, n.id); } catch { /* */ }
    }
    setOpen(false);
    reload();
  }

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={cn(
          'relative grid size-9 place-items-center rounded-xl border border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:bg-zinc-900 hover:text-white',
          hasCritical && 'border-rose-500/60 text-rose-300 hover:text-rose-200',
        )}
        aria-label="Notifications"
      >
        {hasCritical ? <BellRing className="size-4" /> : <Bell className="size-4" />}
        {unread > 0 && (
          <span
            className={cn(
              'absolute -top-1 -right-1 inline-grid min-w-[18px] place-items-center rounded-full px-1 text-[10px] font-semibold ring-2 ring-zinc-950',
              hasCritical ? 'bg-rose-500 text-white' : 'bg-cyan-500 text-white',
            )}
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-40 mt-2 w-[min(420px,90vw)] overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950/95 shadow-2xl backdrop-blur">
          <div className="flex items-center justify-between border-b border-zinc-800 px-4 py-2.5">
            <div className="text-sm font-medium text-zinc-200">Notifications</div>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-zinc-400 hover:text-cyan-300">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-zinc-500">No notifications yet.</div>
            ) : (
              items.map(n => <Row key={n.id} n={n} businessId={businessId} onOpen={() => handleOpen(n)} />)
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ n, businessId, onOpen }: { n: Notification; businessId: string; onOpen: () => void }) {
  const icon =
    n.severity === 'critical' ? <AlertCircle className="size-4 text-rose-400" />
    : n.severity === 'warn'   ? <AlertTriangle className="size-4 text-amber-300" />
    : n.type === 'call_started' ? <Phone className="size-4 text-cyan-300" />
    : n.type === 'job_dispatched' ? <Truck className="size-4 text-emerald-300" />
    : <CheckCircle2 className="size-4 text-zinc-400" />;

  const ts = new Date(n.created_at);
  const tsLabel = ts.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });

  const inner = (
    <div className={cn(
      'flex gap-3 border-b border-zinc-900 px-4 py-3 transition hover:bg-zinc-900/60',
      !n.read_at && 'bg-zinc-900/40',
    )}>
      <div className="mt-0.5 shrink-0">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <div className={cn('text-sm', n.severity === 'critical' ? 'text-rose-200 font-medium' : 'text-zinc-200')}>
            {n.title}
          </div>
          {!n.read_at && <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-cyan-400" />}
        </div>
        {(n.customer_phone || n.job_dispatch_status) && (
          <div className="mt-0.5 text-xs text-zinc-500">
            {n.customer_phone && <span>{n.customer_phone}</span>}
            {n.customer_phone && n.job_dispatch_status && <span> · </span>}
            {n.job_dispatch_status && <span>{dispatchStatusLabel(n.job_dispatch_status)}</span>}
          </div>
        )}
        <div className="mt-1 text-xs text-zinc-500">{tsLabel}</div>
      </div>
    </div>
  );

  if (n.job_id) {
    return (
      <Link to={`/crm/${businessId}/jobs/${n.job_id}`} onClick={onOpen} className="block">
        {inner}
      </Link>
    );
  }
  return <div onClick={onOpen}>{inner}</div>;
}

export function dispatchStatusLabel(s: string | null): string {
  switch (s) {
    case 'delivered_telnyx':    return 'SMS delivered';
    case 'delivered_email_sms': return 'SMS via email gateway';
    case 'delivered_email':     return 'Email backup only — SMS failed';
    case 'delivered_app_only':  return 'IN APP ONLY — owner must check CRM';
    case 'pending':             return 'Not dispatched yet';
    default:                    return s || '';
  }
}
